import React, { useState } from 'react';
import { Box, Button, useMediaQuery, useTheme } from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import { AppointmentStatus } from '../../../types/appointment.types';
import { FilterToggleGroup } from '../../common/FilterToggleGroup';
import { PageToolbar } from '../../common/PageToolbar';
import { PageCard } from '../../common/PageCard';
import { UniversalPagination } from '../../common/UniversalPagination';
import { RescheduleAppointmentDialog } from '../RescheduleAppointmentDialog';
import { ReviewDialog } from '../ReviewDialog';
import { ExpertPickerModal } from '../../calendar/add-event-modals/ExpertPickerModal';
import { ConsultationRequestModal } from '../../experts/ConsultationRequestModal';
import { ExpertListItemDto } from '../../../types/expert';
import { useClientAppointments } from './hooks/useClientAppointments';
import { useClientAppointmentActions } from './hooks/useClientAppointmentActions';
import { useAppointmentDeepLink } from '../hooks/useAppointmentDeepLink';
import { useRefetchOnNotification } from '../hooks/useRefetchOnNotification';
import { ClientAppointmentsTable } from './ClientAppointmentsTable';
import { ClientAppointmentDetailsDialog } from './dialogs/ClientAppointmentDetailsDialog';
import { ClientAppointmentCancelDialog } from './dialogs/ClientAppointmentCancelDialog';

const BASE_STATUS_OPTIONS = [
  { value: '',                            label: 'Всі' },
  { value: AppointmentStatus.PENDING,     label: 'Очікує' },
  { value: AppointmentStatus.CONFIRMED,   label: 'Підтверджено' },
  { value: AppointmentStatus.COMPLETED,   label: 'Завершено' },
  { value: AppointmentStatus.CANCELLED,   label: 'Скасовано' },
];

interface Props {
  expertId?: number;
}

export const ClientAppointmentsList: React.FC<Props> = ({ expertId }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [expertPickerOpen, setExpertPickerOpen] = useState(false);
  const [bookingExpert, setBookingExpert] = useState<ExpertListItemDto | null>(null);

  const {
    appointments, total, loading, options, fetchAppointments,
    statusFilter, expertFilter,
    onStatusChange, onExpertChange,
    page, limit, onPageChange, onLimitChange,
    handleReset,
  } = useClientAppointments(expertId);

  const pendingCount = options.statusCounts?.pending ?? 0;

  const actions = useClientAppointmentActions(fetchAppointments);

  // Live-оновлення списку при отриманні нової notification (наприклад, експерт підтвердив).
  useRefetchOnNotification(fetchAppointments);

  // Deep-link: `/appointments?id=123` → автовідкриття модалки. Дозволяє ділитися посиланнями.
  const { openWithUrl: openDetailsWithUrl, closeWithUrl: closeDetailsWithUrl } =
    useAppointmentDeepLink(actions.selected?.id ?? null, actions.handleOpenDetails, actions.handleCloseDetails);

  const expertOptions = options.experts.map(e => ({ value: String(e.id), label: e.name }));

  const statusOptions = BASE_STATUS_OPTIONS.map(opt =>
    opt.value === AppointmentStatus.PENDING
      ? { ...opt, badgeCount: pendingCount }
      : opt,
  );

  const toolbarFilters = [
    ...(!expertId && expertOptions.length > 0 ? [{
      id: 'expertId',
      type: 'select' as const,
      label: 'Експерт',
      value: expertFilter,
      onChange: onExpertChange,
      options: expertOptions,
    }] : []),
    {
      id: 'status',
      type: 'custom' as const,
      value: statusFilter,
      onChange: onStatusChange,
      renderCustom: () => (
        <FilterToggleGroup
          value={statusFilter}
          onChange={onStatusChange}
          options={statusOptions}
        />
      ),
    },
  ];

  return (
    <Box>
      <PageCard sx={{ mb: 2 }}>
        <PageToolbar
          storageKey={expertId ? `filters:expert-${expertId}-client-appointments` : 'filters:client-appointments'}
          filters={toolbarFilters}
          onReset={handleReset}
          showReset={!expertId}
          containerSx={{ flexWrap: 'nowrap' }}
          extraActions={!expertId && (
            <Button
              variant="contained"
              onClick={() => setExpertPickerOpen(true)}
              sx={{
                minWidth: isMobile ? '40px' : 'auto',
                p: isMobile ? 1 : undefined,
                height: '40px',
              }}
            >
              <AddIcon sx={{ mr: isMobile ? 0 : 1 }} />
              {!isMobile && 'Нова зустріч'}
            </Button>
          )}
        />
      </PageCard>

      <PageCard sx={{ p: 0, overflow: 'hidden' }}>
        <ClientAppointmentsTable
          appointments={appointments}
          loading={loading}
          isMobile={isMobile}
          onOpenDetails={openDetailsWithUrl}
          onQuickConfirm={actions.handleConfirm}
          onOpenCancel={actions.openCancelDialog}
          onOpenReschedule={actions.openRescheduleDialog}
          onOpenReview={actions.openReviewDialog}
        />
        <Box sx={{ p: 2, borderTop: '1px solid', borderColor: 'divider' }}>
          <UniversalPagination
            page={page}
            count={Math.ceil(total / limit)}
            limit={limit}
            total={total}
            onPageChange={onPageChange}
            onLimitChange={onLimitChange}
            storageKey="pagination:client-appointments"
          />
        </Box>
      </PageCard>

      <ClientAppointmentDetailsDialog
        selected={actions.selected}
        modalLoading={actions.modalLoading}
        onClose={closeDetailsWithUrl}
        onQuickConfirm={actions.handleConfirm}
        onOpenCancel={actions.openCancelDialog}
        onOpenReschedule={actions.openRescheduleDialog}
        onOpenReview={actions.openReviewDialog}
      />

      <ClientAppointmentCancelDialog
        target={actions.cancelTarget}
        cancelling={actions.cancelling}
        onConfirm={actions.handleCancel}
        onClose={actions.closeCancelDialog}
      />

      {actions.rescheduleTarget && (
        <RescheduleAppointmentDialog
          open
          onClose={actions.closeRescheduleDialog}
          onSuccess={() => {
            actions.closeRescheduleDialog();
            if (actions.selected?.id === actions.rescheduleTarget?.id) actions.handleCloseDetails();
            fetchAppointments();
          }}
          appointmentId={actions.rescheduleTarget.id}
          expertId={actions.rescheduleTarget.expert.id}
          expertName={actions.rescheduleTarget.expert.name}
        />
      )}

      {actions.reviewTarget && (
        <ReviewDialog
          open
          appointment={actions.reviewTarget}
          onClose={actions.closeReviewDialog}
          onSuccess={(updated) => {
            actions.closeReviewDialog();
            fetchAppointments();
            if (actions.selected?.id === updated.id) {
              actions.handleOpenDetails(updated.id);
            }
          }}
        />
      )}

      <ExpertPickerModal
        open={expertPickerOpen}
        onClose={() => setExpertPickerOpen(false)}
        onSelect={expert => {
          setExpertPickerOpen(false);
          setBookingExpert(expert);
        }}
      />
      {bookingExpert && (
        <ConsultationRequestModal
          expert={bookingExpert}
          open
          onClose={isSuccess => {
            setBookingExpert(null);
            if (isSuccess) fetchAppointments();
          }}
        />
      )}
    </Box>
  );
};
